import { useQuery, useMutation } from '@common/hooks';

import { createMessage } from 'works/legacy/lib/message-helper';

import {
  SPEARFISHABLE_WORKERS_BY_SHIFT_GAP,
  SPEARFISHABLE_GROUPS_BY_SHIFT_GAP,
} from '@scheduling/graphql/queries/spearfishing';
import { SHIFT_DETAILS_QUERY } from '@scheduling/graphql';
import { CREATE_SPEARFISHING_CAMPAIGN } from '@scheduling/graphql/mutations/spearfishing';
import { ENUM_QUERY } from '@common/graphql';

export const GROUP_MESSAGING_TYPES = ['workforce_groups'];

export type MessageType =
  | 'individuals'
  | 'home_unit'
  | 'workforce_groups'
  | 'preference_match';

export type SpearfishableGroup = {
  spearfishableGroupsByShiftGap: {
    id: string;
    name: string;
    count: number;
    custom: boolean;
    spearfishable: boolean;
  }[];
};

export type CreateSpearfishingCampaign = {
  createSpearfishingCampaign: {
    id: string;
    errors: string[];
  };
};

export type SpearfishableWorker = {
  spearfishableWorkersByShiftGap: {
    worker: {
      id: string;
      worksUser: {
        id: string;
        name: string;
      };
      workforceGroup: {
        name: string;
      };
    };
    spearfishingDetail: string | null;
    spearfishable: boolean;
  }[];
};

export const useSpearfishingData = ({
  shiftId,
  groupMessagingEnabled,
}: {
  shiftId: string;
  groupMessagingEnabled: boolean;
}) => {
  const { data: messageTypeData, loading: messageTypeLoading } = useQuery(
    ENUM_QUERY,
    {
      variables: { key: 'SpearfishingMessageTypeDescription' },
      fetchPolicy: 'no-cache',
      skip: !shiftId,
    }
  );

  const {
    data: individualsSpearfishableWorkerData,
    loading: individualsSpearfishableWorkerLoading,
  } = useQuery(SPEARFISHABLE_WORKERS_BY_SHIFT_GAP, {
    variables: {
      shiftGapId: shiftId,
      filters: { messageType: 'individuals' },
    },
    fetchPolicy: 'no-cache',
    skip: !shiftId,
  });

  const {
    data: homeUnitSpearfishableWorkerData,
    loading: homeUnitSpearfishableWorkerLoading,
  } = useQuery(SPEARFISHABLE_WORKERS_BY_SHIFT_GAP, {
    variables: {
      shiftGapId: shiftId,
      filters: { messageType: 'home_unit' },
    },
    fetchPolicy: 'no-cache',
    skip: !shiftId,
  });

  const {
    data: preferenceMatchSpearfishableWorkerData,
    loading: preferenceMatchSpearfishableWorkerLoading,
  } = useQuery(SPEARFISHABLE_WORKERS_BY_SHIFT_GAP, {
    variables: {
      shiftGapId: shiftId,
      filters: { messageType: 'preference_match' },
    },
    fetchPolicy: 'no-cache',
    skip: !shiftId,
  });

  const { data: spearfishableGroupData, loading: spearfishableGroupLoading } =
    useQuery(SPEARFISHABLE_GROUPS_BY_SHIFT_GAP, {
      variables: { shiftGapId: shiftId },
      fetchPolicy: 'no-cache',
      skip: !shiftId || !groupMessagingEnabled,
    });

  const spearfishingDataLoading = [
    messageTypeLoading,
    individualsSpearfishableWorkerLoading,
    homeUnitSpearfishableWorkerLoading,
    spearfishableGroupLoading,
    preferenceMatchSpearfishableWorkerLoading,
  ].some((loading) => loading);

  return {
    messageTypeData: messageTypeData?.enum,
    individualsSpearfishableWorkerData,
    homeUnitSpearfishableWorkerData,
    spearfishableGroupData,
    spearfishingDataLoading,
    preferenceMatchSpearfishableWorkerData,
  };
};

export const useShiftGapData = ({
  shiftId,
  sourcePage,
}: {
  shiftId: string;
  sourcePage: string;
}) => {
  const { data, loading, error } = useQuery(SHIFT_DETAILS_QUERY, {
    variables: { id: shiftId, sourcePage },
    skip: !shiftId,
  });

  return {
    shiftGapData: data?.shiftGap,
    shiftGapLoading: loading,
    shiftGapError: error,
  };
};

export const useCreateSpearfishingMutation = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const showErrorMessage = () => {
    createMessage(
      'error',
      'Failed to send shift. Please try again or contact your hiring partner.'
    );
  };

  const [createSpearfishing, { loading: createLoading }] = useMutation(
    CREATE_SPEARFISHING_CAMPAIGN,
    {
      onCompleted: (result) => {
        if (result?.createSpearfishingCampaign === null) {
          showErrorMessage();
        } else {
          createMessage('success', 'Shift sent successfully');
          onClose();
        }
      },
      onError: () => {
        showErrorMessage();
      },
      refetchQueries: [
        'SpearfishableWorkersByShiftGap',
        'SpearfishableGroupsByShiftGap',
        'PaginatedSpearfishingCampaigns',
      ],
    }
  );

  return { createSpearfishing, createLoading };
};
